/**
 * SOFSEK — Violations Tracker & Breach Disposition Wizard (§7, §37). Every
 * violation carries stable identity + auto-resolution (Phase 3/4 engine).
 * Selecting a row opens a centered, tabbed modal: Tab 1 shows the 3-level
 * lineage (rule → bundled elements → account lineage, "Granted By" listing
 * every contributing role); Tab 2 is the accept/mitigate form. If this
 * dedupe_key was previously accepted and resolved, the prior acceptance shows
 * as context (§37 — it never silently covers the recurrence).
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { useAdaptiveList } from '../../lib/use_adaptive_list';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { getForensicActorId } from '../../types/auth';
import { hasAccess, type AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { EmptyState } from '../../components/common/EmptyState';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useFormatDate, useFormatDateTime } from '../../lib/format_date';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { RefreshCw, Search, Play } from 'lucide-react';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const PAGE_CODE = 'SFK.VIOLATION.LIST';
const inputCls = 'mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm';
const labelCls = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';

interface CodeOpt { code: string; name: string; }
interface ViolationRow {
  violation_id: number;
  dedupe_key: string;
  rule_id: number;
  app_user_id: number | null;
  self_user_id: number | null;
  violation_status_code: string;
  breach_start_at: string;
  breach_end_at: string | null;
}
interface SourceRow {
  side_ordinal: 1 | 2;
  permission_id: number | null;
  role_id: number | null;
  self_permission_id: number | null;
  self_role_id: number | null;
}

function tlName(list: unknown, fallback: string): string {
  const arr = (list ?? []) as { locale_code: string; display_text: string }[];
  return arr.find(x => x.locale_code === 'EN_US')?.display_text ?? fallback;
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'rule', header: 'Rule', defaultWidth: 220 },
  { key: 'holder', header: 'Subject / Operator', defaultWidth: 200 },
  { key: 'status', header: 'Status', defaultWidth: 160 },
  { key: 'breach_start_at', header: 'Detected', defaultWidth: 170 },
];

function ViolationInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [rules, setRules] = useState<CodeOpt[]>([]);
  const [statuses, setStatuses] = useState<CodeOpt[]>([]);
  const [subjects, setSubjects] = useState<Map<number, string>>(new Map());
  const [users, setUsers] = useState<Map<number, string>>(new Map());
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [ruleFacet, setRuleFacet] = useState<string[]>([]);
  const [statusFacet, setStatusFacet] = useState<string[]>(['ACTIVE_VIOLATION', 'PROVISIONALLY_ACCEPTED']);
  const [selected, setSelected] = useState<ViolationRow | null>(null);
  const [scanning, setScanning] = useState(false);

  // Small code lookups load once (rules + statuses resolve names / fill facets).
  const loadLookups = async () => {
    const [ruleRes, statusRes] = await Promise.all([
      apiDb.from('sfk_conflict_rule').select('rule_id, sfk_conflict_rule_tl ( locale_code, display_text )'),
      apiDb.from('sfk_violation_status').select('violation_status_code, sfk_violation_status_tl ( locale_code, display_text )'),
    ]);
    const err = ruleRes.error ?? statusRes.error;
    if (err) { setLookupError(err.message); return; }
    setRules(((ruleRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: String(r.rule_id), name: tlName(r.sfk_conflict_rule_tl, String(r.rule_id)) })));
    setStatuses(((statusRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.violation_status_code as string, name: tlName(r.sfk_violation_status_tl, r.violation_status_code as string) })));
  };
  useEffect(() => { void loadLookups(); }, []);

  // Adaptive main list — facets server-side; text search (over resolved rule/holder
  // names, not base-table columns) stays client-side over the loaded page.
  const mapRow = useCallback((r: Record<string, unknown>) => r as unknown as ViolationRow, []);
  const facets = useMemo(() => [
    { column: 'rule_id', values: ruleFacet.map(Number) },
    { column: 'violation_status_code', values: statusFacet },
  ], [ruleFacet, statusFacet]);
  const { rows, total, truncated, busy, error: listError, reload } =
    useAdaptiveList<Record<string, unknown>, ViolationRow>({
      table: 'sfk_violation',
      columns: 'violation_id, dedupe_key, rule_id, app_user_id, self_user_id, violation_status_code, breach_start_at, breach_end_at',
      order: { column: 'breach_start_at', ascending: false },
      search: '',
      searchColumns: [],
      facets,
      map: mapRow,
    });
  const error = listError ?? lookupError;

  // Resolve only the page's referenced subject/operator ids (scoped .in()).
  useEffect(() => {
    const subjIds = [...new Set(rows.map(r => r.app_user_id).filter((v): v is number => v != null))];
    const userIds = [...new Set(rows.map(r => r.self_user_id).filter((v): v is number => v != null))];
    let cancelled = false;
    void (async () => {
      if (subjIds.length) {
        const res = await apiDb.from('sfk_subject_profile').select('user_id, csm_user_master!fk_sfk_subject_profile_user ( display_name )').in('user_id', subjIds);
        if (!cancelled && !res.error) setSubjects(new Map(((res.data ?? []) as Record<string, unknown>[]).map(r => [r.user_id as number, ((r.csm_user_master ?? {}) as { display_name?: string }).display_name ?? String(r.user_id)])));
      }
      if (userIds.length) {
        const res = await apiDb.from('csm_user_master').select('user_id, display_name').in('user_id', userIds);
        if (!cancelled && !res.error) setUsers(new Map(((res.data ?? []) as Record<string, unknown>[]).map(r => [r.user_id as number, r.display_name as string])));
      }
    })();
    return () => { cancelled = true; };
  }, [rows]);

  const runScanAll = async () => {
    setScanning(true); setLookupError(null);
    try {
      const { error: e } = await apiDb.rpc('sfk_run_conflict_scan_all');
      if (e) throw e;
      reload();
    } catch (e) { setLookupError((e as Error).message); } finally { setScanning(false); }
  };

  const detail = useExpandedRows<number>();
  // §6/§17: csm_role_page_access decides which write affordances this user is
  // offered. The server refuses the write regardless (RLS, via the Common
  // Routine) — this stops the page offering an action that can only error.
  const canAdd    = hasAccess(session, PAGE_CODE, 'add');
  const ruleName = (id: number) => rules.find(r => r.code === String(id))?.name ?? String(id);
  const resolveNameForStatus = (code: string) => statuses.find(s => s.code === code)?.name ?? code;
  const holderName = (r: ViolationRow) =>
    r.app_user_id != null ? (subjects.get(r.app_user_id) ?? String(r.app_user_id))
    : `${t('sfk.violation.self_operator', 'SOFSEK operator')}: ${users.get(r.self_user_id ?? -1) ?? String(r.self_user_id)}`;

  // §17.B13 — related records for a violation: the conflict rule it fired and the
  // subject/operator who holds the conflicting access (resolved names, §17.B4).
  const buildRelatedGroups = (r: ViolationRow): RelatedChildGroup[] => [
    { key: 'rule', title: t('sfk.violation.related.rule', 'Conflict rule'),
      items: [{ id: r.rule_id, label: ruleName(r.rule_id), title: r.dedupe_key }] },
    { key: 'holder', title: t('sfk.violation.related.holder', 'Subject / Operator'),
      items: (r.app_user_id != null || r.self_user_id != null) ? [{ id: 'holder', label: holderName(r) }] : [] },
  ];
  const hasRelated = (r: ViolationRow) => buildRelatedGroups(r).some(g => g.items.length > 0);

  // Facets applied server-side by the hook; only the text search (over resolved
  // rule/holder names) is client-side, over the loaded page.
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => ruleName(r.rule_id).toLowerCase().includes(q) || holderName(r).toLowerCase().includes(q));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filter, rules, subjects, users]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('sfk.violation.title', 'Violations Tracker')}
          description={t('sfk.violation.description', 'Detected segregation-of-duties breaches — accept, mitigate, or track to resolution.')}
          actions={<>
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
            {canAdd && (<button type="button" onClick={() => void runScanAll()} disabled={scanning} className="btn-primary flex items-center gap-1.5 disabled:opacity-60"><Play className="h-3.5 w-3.5" /> {scanning ? t('sfk.violation.scanning', 'Scanning…') : t('sfk.violation.run_scan', 'Run All Scans')}</button>)}
          </>}
        />
        <StatStrip stats={[
          { value: total, label: t('sfk.violation.stat.total', 'Violations') },
          { value: filtered.length, label: t('sfk.violation.stat.shown', 'Shown') },
        ]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('sfk.violation.facet.rule', 'Rule')} options={rules.map(r => ({ value: r.code, label: r.name }))} selected={ruleFacet} onChange={setRuleFacet} />
            <FacetFilter label={t('sfk.violation.facet.status', 'Status')} options={statuses.map(s => ({ value: s.code, label: s.name }))} selected={statusFacet} onChange={setStatusFacet} />
            <ClearFiltersButton active={filter.trim() !== '' || ruleFacet.length > 0 || statusFacet.length !== 2} onClear={() => { setFilter(''); setRuleFacet([]); setStatusFacet([]); }} />
            <div className="ml-auto flex items-center gap-2">
              {truncated && !busy && (
                <span className="text-csm-xs text-csm-muted">{t('common.list.truncated', 'Showing first {{n}} of {{total}} — narrow with facets', { n: rows.length, total })}</span>
              )}
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="border-b border-csm-border px-1 py-1.5 text-left" style={{ width: '1%' }} aria-hidden>&nbsp;</th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.violation_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.violation_id)} hasChildren={hasRelated(r)} onToggle={() => detail.toggle(r.violation_id)}
                        expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'rule' && ruleName(r.rule_id)}
                        {col.key === 'holder' && holderName(r)}
                        {col.key === 'status' && (
                          <span className={`rounded px-1.5 py-0.5 text-csm-xs ${r.violation_status_code === 'ACTIVE_VIOLATION' ? 'bg-csm-danger-soft text-csm-danger' : r.violation_status_code === 'PROVISIONALLY_ACCEPTED' ? 'bg-csm-warning-soft text-csm-warning' : 'bg-csm-success-soft text-csm-success'}`}>
                            {resolveNameForStatus(r.violation_status_code)}
                          </span>
                        )}
                        {col.key === 'breach_start_at' && formatDateTime(r.breach_start_at)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                      <button onClick={() => setSelected(r)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('sfk.violation.view', 'View')}</button>
                    </td>
                  </tr>
                  {detail.isOpen(r.violation_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r)} emptyLabel={t('common.related.none', 'No related records.')} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {selected && (
        <DispositionModal session={session} row={selected} ruleName={ruleName(selected.rule_id)} holderName={holderName(selected)}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); void reload(); }} />
      )}
    </div>
  );
}

function DispositionModal({ session, row, ruleName, holderName, onClose, onSaved }: {
  session: AuthSession; row: ViolationRow; ruleName: string; holderName: string; onClose: () => void; onSaved: () => void;
}) {
  // Reached via "View", which is a read action — so the accept/mitigate write
  // is gated here rather than at the entry point.
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const formatDateTime = useFormatDateTime();
  const [activeTab, setActiveTab] = useState('lineage');
  const [sources, setSources] = useState<SourceRow[]>([]);
  const [permNames, setPermNames] = useState<Map<number, string>>(new Map());
  const [roleNames, setRoleNames] = useState<Map<number, string>>(new Map());
  const [reasons, setReasons] = useState<CodeOpt[]>([]);
  const [controls, setControls] = useState<CodeOpt[]>([]);
  const [templates, setTemplates] = useState<{ code: string; name: string; reason: string | null; controls: string[] }[]>([]);
  const [reviewers, setReviewers] = useState<CodeOpt[]>([]);
  const [priorException, setPriorException] = useState<{ reason: string; notes: string | null; approver: string | null; expiration: string } | null>(null);
  const [currentException, setCurrentException] = useState<{ violation_exception_id: number; reason_code: string; notes: string | null; expiration_date: string; controls: string[] } | null>(null);

  const [reasonCode, setReasonCode] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedControls, setSelectedControls] = useState<string[]>([]);
  const [reviewerId, setReviewerId] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const [srcRes, reasonRes, controlRes, tmplRes, tmplCtlRes, userRes] = await Promise.all([
        apiDb.from('sfk_violation_permission_source').select('side_ordinal, permission_id, role_id, self_permission_id, self_role_id').eq('violation_id', row.violation_id),
        apiDb.from('sfk_reason').select('reason_code, sfk_reason_tl ( locale_code, display_text )'),
        apiDb.from('sfk_mitigating_action').select('mitigating_action_code, sfk_mitigating_action_tl ( locale_code, display_text )'),
        apiDb.from('sfk_mitigation_template').select('template_id, internal_code, reason_code, sfk_mitigation_template_tl ( locale_code, display_text )').eq('is_active', 'Y'),
        apiDb.from('sfk_mitigation_template_control').select('template_id, mitigating_action_code'),
        apiDb.from('csm_user_master').select('user_id, display_name').eq('is_active', 'Y'),
      ]);
      const src = (srcRes.data ?? []) as unknown as SourceRow[];
      setSources(src);

      const permIds = src.filter(s => s.permission_id != null).map(s => s.permission_id as number);
      const selfPermIds = src.filter(s => s.self_permission_id != null).map(s => s.self_permission_id as number);
      const roleIds = src.filter(s => s.role_id != null).map(s => s.role_id as number);
      const selfRoleIds = src.filter(s => s.self_role_id != null).map(s => s.self_role_id as number);

      const [permRes, selfPermRes, roleRes, selfRoleRes] = await Promise.all([
        permIds.length ? apiDb.from('sfk_v_action').select('permission_id, display_name').in('permission_id', permIds) : Promise.resolve({ data: [], error: null }),
        selfPermIds.length ? apiDb.from('csm_page_master').select('page_id, internal_code').in('page_id', selfPermIds) : Promise.resolve({ data: [], error: null }),
        roleIds.length ? apiDb.from('sfk_external_role').select('role_id, name').in('role_id', roleIds) : Promise.resolve({ data: [], error: null }),
        selfRoleIds.length ? apiDb.from('csm_role_master_tl').select('role_id, display_text').eq('locale_code', 'EN_US').in('role_id', selfRoleIds) : Promise.resolve({ data: [], error: null }),
      ]);
      const pMap = new Map<number, string>();
      ((permRes.data ?? []) as { permission_id: number; display_name: string }[]).forEach(p => pMap.set(p.permission_id, p.display_name));
      ((selfPermRes.data ?? []) as { page_id: number; internal_code: string }[]).forEach(p => pMap.set(p.page_id, p.internal_code));
      setPermNames(pMap);
      const rMap = new Map<number, string>();
      ((roleRes.data ?? []) as { role_id: number; name: string }[]).forEach(r => rMap.set(r.role_id, r.name));
      ((selfRoleRes.data ?? []) as { role_id: number; display_text: string }[]).forEach(r => rMap.set(r.role_id, r.display_text));
      setRoleNames(rMap);

      setReasons(((reasonRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.reason_code as string, name: tlName(r.sfk_reason_tl, r.reason_code as string) })));
      setControls(((controlRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.mitigating_action_code as string, name: tlName(r.sfk_mitigating_action_tl, r.mitigating_action_code as string) })));
      setReviewers(((userRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: String(r.user_id), name: r.display_name as string })));

      const tmplCtlMap = new Map<number, string[]>();
      ((tmplCtlRes.data ?? []) as { template_id: number; mitigating_action_code: string }[]).forEach(tc => {
        const arr = tmplCtlMap.get(tc.template_id) ?? [];
        arr.push(tc.mitigating_action_code);
        tmplCtlMap.set(tc.template_id, arr);
      });
      setTemplates(((tmplRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
        code: String(r.template_id), name: tlName(r.sfk_mitigation_template_tl, r.internal_code as string),
        reason: (r.reason_code as string | null), controls: tmplCtlMap.get(r.template_id as number) ?? [],
      })));

      // §37: prior acceptance context if this dedupe_key was accepted+resolved before.
      const priorRes = await apiDb.from('sfk_violation').select('violation_id').eq('dedupe_key', row.dedupe_key).eq('violation_status_code', 'RESOLVED').neq('violation_id', row.violation_id).order('breach_end_at', { ascending: false }).limit(1);
      const priorIds = ((priorRes.data ?? []) as { violation_id: number }[]).map(p => p.violation_id);
      if (priorIds.length) {
        const exRes = await apiDb.from('sfk_violation_exception').select('reason_code, notes, expiration_date, approver_id').eq('violation_id', priorIds[0]).order('created_at', { ascending: false }).limit(1);
        const ex = ((exRes.data ?? []) as { reason_code: string; notes: string | null; expiration_date: string; approver_id: number | null }[])[0];
        if (ex) setPriorException({ reason: ex.reason_code, notes: ex.notes, approver: ex.approver_id != null ? String(ex.approver_id) : null, expiration: ex.expiration_date });
      }

      // Current exception, if this violation is already PROVISIONALLY_ACCEPTED.
      if (row.violation_status_code === 'PROVISIONALLY_ACCEPTED') {
        const curRes = await apiDb.from('sfk_violation_exception').select('violation_exception_id, reason_code, notes, expiration_date').eq('violation_id', row.violation_id).order('created_at', { ascending: false }).limit(1);
        const cur = ((curRes.data ?? []) as { violation_exception_id: number; reason_code: string; notes: string | null; expiration_date: string }[])[0];
        if (cur) {
          const ctlRes = await apiDb.from('sfk_exception_control').select('mitigating_action_code').eq('violation_exception_id', cur.violation_exception_id);
          setCurrentException({ ...cur, controls: ((ctlRes.data ?? []) as { mitigating_action_code: string }[]).map(c => c.mitigating_action_code) });
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.violation_id]);

  const resolveNameForReason = (code: string) => reasons.find(r => r.code === code)?.name ?? code;
  const resolveNameForControl = (code: string) => controls.find(c => c.code === code)?.name ?? code;

  const applyTemplate = (templateCode: string) => {
    const tmpl = templates.find(x => x.code === templateCode);
    if (!tmpl) return;
    if (tmpl.reason) setReasonCode(tmpl.reason);
    setSelectedControls(tmpl.controls);
  };

  const toggleControl = (code: string) => setSelectedControls(cur => cur.includes(code) ? cur.filter(x => x !== code) : [...cur, code]);

  const accept = async () => {
    setBusy(true); setError(null);
    try {
      if (!reasonCode) throw new Error(t('sfk.violation.err.reason_required', 'A reason code is required.'));
      if (selectedControls.length === 0) throw new Error(t('sfk.violation.err.control_required', 'At least one mitigating control is required.'));
      if (!reviewerId) throw new Error(t('sfk.violation.err.reviewer_required', 'An independent reviewer is required.'));
      if (!expirationDate) throw new Error(t('sfk.violation.err.expiration_required', 'An expiration date is required.'));

      const { row_id, error: e } = await routedInsert(session, 'sfk_violation_exception', {
        violation_id: row.violation_id,
        reason_code: reasonCode,
        notes: notes.trim() || null,
        approver_id: getForensicActorId(session),
        approval_timestamp: new Date().toISOString(),
        independent_reviewer_id: Number(reviewerId),
        expiration_date: expirationDate,
      });
      if (e) throw new Error(e.message);
      const exceptionId = row_id;
      if (exceptionId != null) {
        await Promise.all(selectedControls.map(c => routedInsert(session, 'sfk_exception_control', { violation_exception_id: exceptionId, mitigating_action_code: c })));
      }
      const { error: e2 } = await routedUpdate(session, 'sfk_violation', 'violation_id', row.violation_id, { violation_status_code: 'PROVISIONALLY_ACCEPTED' });
      if (e2) throw new Error(e2.message);
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const sideAPerms = sources.filter(s => s.side_ordinal === 1);
  const sideBPerms = sources.filter(s => s.side_ordinal === 2);
  const nameFor = (s: SourceRow) => permNames.get((s.permission_id ?? s.self_permission_id) as number) ?? '—';
  const roleFor = (s: SourceRow) => roleNames.get((s.role_id ?? s.self_role_id) as number) ?? '—';

  return (
    <Modal
      title={`${t('sfk.violation.disposition_title', 'Breach Disposition')} — ${ruleName}`}
      onClose={onClose}
      width="lg"
      headerAction={<PersonalizationToggle />}
      tabs={[
        { id: 'lineage', label: t('sfk.violation.tab_lineage', 'Lineage') },
        { id: 'disposition', label: t('sfk.violation.tab_disposition', 'Mitigation & Sign-off'), disabled: row.violation_status_code === 'RESOLVED' },
      ]}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      footer={
        activeTab === 'disposition' && row.violation_status_code === 'ACTIVE_VIOLATION' ? (
          <>
            <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
            {canChange && (<button onClick={() => void accept()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('sfk.violation.accept', 'Accept & Mitigate')}</button>)}
          </>
        ) : (
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.close', 'Close')}</button>
        )
      }
    >
      {activeTab === 'lineage' && (
        <div>
          <div className="mb-3 rounded border border-csm-border-soft p-2">
            <div className="text-csm-xs uppercase tracking-wider text-csm-watermark">{t('sfk.violation.level1', 'Level 1 — The Rule')}</div>
            <div className="text-csm-sm">{ruleName}</div>
          </div>
          <div className="mb-3 rounded border border-csm-border-soft p-2">
            <div className="text-csm-xs uppercase tracking-wider text-csm-watermark">{t('sfk.violation.level2', 'Level 2 — Bundled Elements')}</div>
            <div className="mt-1 grid grid-cols-2 gap-3">
              <div>
                <div className="text-csm-xs font-medium text-csm-ink-soft">{t('sfk.conflict_rule.bundle_a', 'Bundle A')}</div>
                {sideAPerms.map((s, i) => <div key={i} className="text-csm-sm">{nameFor(s)}</div>)}
              </div>
              <div>
                <div className="text-csm-xs font-medium text-csm-ink-soft">{t('sfk.conflict_rule.bundle_b', 'Bundle B')}</div>
                {sideBPerms.map((s, i) => <div key={i} className="text-csm-sm">{nameFor(s)}</div>)}
              </div>
            </div>
          </div>
          <div className="rounded border border-csm-border-soft p-2">
            <div className="text-csm-xs uppercase tracking-wider text-csm-watermark">{t('sfk.violation.level3', 'Level 3 — Account Lineage (Granted By)')}</div>
            <div className="mt-1 text-csm-sm">{holderName}</div>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div>
                {sideAPerms.map((s, i) => <div key={i} className="text-csm-xs text-csm-watermark">{nameFor(s)} ← {roleFor(s)}</div>)}
              </div>
              <div>
                {sideBPerms.map((s, i) => <div key={i} className="text-csm-xs text-csm-watermark">{nameFor(s)} ← {roleFor(s)}</div>)}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'disposition' && (
        <div>
          {priorException && (
            <div className="mb-3 rounded border border-csm-warning-border bg-csm-warning-soft px-2 py-1.5 text-csm-xs text-csm-warning">
              {t('sfk.violation.prior_context', 'This combination was previously accepted and later resolved — recurrence, not a covered acceptance.')}
              <div className="mt-1">{t('sfk.violation.prior_reason', 'Prior reason')}: {resolveNameForReason(priorException.reason)} · {t('sfk.violation.prior_expiration', 'expired')} {formatDate(priorException.expiration)}</div>
              {priorException.notes && <div>{priorException.notes}</div>}
            </div>
          )}

          {currentException ? (
            <div className="rounded border border-csm-border-soft p-2 text-csm-sm">
              <div><strong>{t('sfk.violation.reason', 'Reason')}:</strong> {resolveNameForReason(currentException.reason_code)}</div>
              {currentException.notes && <div><strong>{t('sfk.violation.notes', 'Notes')}:</strong> {currentException.notes}</div>}
              <div><strong>{t('sfk.violation.expiration', 'Expires')}:</strong> {formatDate(currentException.expiration_date)}</div>
              <div><strong>{t('sfk.violation.controls', 'Controls')}:</strong> {currentException.controls.map(resolveNameForControl).join(", ")}</div>
            </div>
          ) : (
            <>
              {templates.length > 0 && (
                <div className="mb-3">
                  <label className={labelCls}>{t('sfk.violation.apply_template', 'Apply Mitigation Template')}</label>
                  <select onChange={e => e.target.value && applyTemplate(e.target.value)} defaultValue="" className={`${inputCls} bg-white`}>
                    <option value="">{t('sfk.violation.select_template', '— choose a template —')}</option>
                    {templates.map(tm => <option key={tm.code} value={tm.code}>{tm.name}</option>)}
                  </select>
                </div>
              )}
              <PersonalizedField componentKey="modal" fieldKey="reason_code" label={t('sfk.violation.reason', 'Reason')}>
                <label className={labelCls}>{t('sfk.violation.reason', 'Reason')}</label>
                <select value={reasonCode} onChange={e => setReasonCode(e.target.value)} className={`${inputCls} bg-white`}>
                  <option value="">{t('sfk.violation.select_reason', '— select —')}</option>
                  {reasons.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
                </select>
              </PersonalizedField>
              <PersonalizedField componentKey="modal" fieldKey="notes" label={t('sfk.violation.notes', 'Notes')}>
                <label className={labelCls}>{t('sfk.violation.notes', 'Notes')}</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="mb-3 w-full resize-y rounded border border-csm-border px-2 py-1 text-csm-sm" />
              </PersonalizedField>
              <PersonalizedField componentKey="modal" fieldKey="controls" label={t('sfk.violation.controls', 'Mitigating Controls')}>
                <label className={labelCls}>{t('sfk.violation.controls', 'Mitigating Controls')}</label>
                <div className="mb-3 max-h-32 overflow-auto rounded border border-csm-border">
                  {controls.map(c => (
                    <label key={c.code} className="flex items-center gap-2 border-b border-csm-border-soft px-2 py-1 text-csm-sm last:border-b-0">
                      <input type="checkbox" checked={selectedControls.includes(c.code)} onChange={() => toggleControl(c.code)} className="h-4 w-4 accent-gray-700" />
                      {c.name}
                    </label>
                  ))}
                </div>
              </PersonalizedField>
              <div className="grid grid-cols-2 gap-x-3">
                <PersonalizedField componentKey="modal" fieldKey="independent_reviewer_id" label={t('sfk.violation.reviewer', 'Independent Reviewer')}>
                  <label className={labelCls}>{t('sfk.violation.reviewer', 'Independent Reviewer')}</label>
                  <select value={reviewerId} onChange={e => setReviewerId(e.target.value)} className={`${inputCls} bg-white`}>
                    <option value="">{t('sfk.violation.select_reviewer', '— select —')}</option>
                    {reviewers.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
                  </select>
                </PersonalizedField>
                <PersonalizedField componentKey="modal" fieldKey="expiration_date" label={t('sfk.violation.expiration', 'Expires')}>
                  <label className={labelCls}>{t('sfk.violation.expiration', 'Expires')}</label>
                  <input type="date" value={expirationDate} onChange={e => setExpirationDate(e.target.value)} className={inputCls} />
                </PersonalizedField>
              </div>
            </>
          )}
          {row.violation_status_code === 'RESOLVED' && row.breach_end_at && (
            <div className="text-csm-xs text-csm-watermark">{t('sfk.violation.resolved_at', 'Resolved')}: {formatDateTime(row.breach_end_at)}</div>
          )}
          {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
        </div>
      )}
    </Modal>
  );
}

export function SfkViolationPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <ViolationInner session={session} />
    </PersonalizationProvider>
  );
}
